import { COMMITTENTE_DECLARATION_LABELS, type CommittenteRule } from '@easyhelp/shared';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { SettingsSection } from '../coda/SettingsSection';
import { formatDateTime } from '../format';

export interface CommittentiSectionProps {
  committenti: CommittenteRule[];
  busy?: boolean;
  error?: string | null;
  onToggle: (rule: CommittenteRule, allowRecordReference: boolean) => void;
}

/**
 * Regola «riferimento al record consentito» per committente (easycare DEC-101, DEC-014), con il
 * default sicuro: vale per l'identificativo paziente di EasyCare e per il riferimento al record.
 */
export function CommittentiSection({ committenti, busy = false, error = null, onToggle }: CommittentiSectionProps) {
  return (
    <SettingsSection
      id="committenti-title"
      title="Committenti"
      description="Per ogni committente dei consumer del prodotto (e per quelli non più dichiarati che hanno una regola salvata), se la segnalazione può portare il riferimento al record su cui l'utente lavorava: l'identificativo interno del paziente per EasyCare, il riferimento con il suo tipo per gli altri prodotti."
    >
      <Alert severity="info">
        Il default è <strong>«non consentito»</strong>: vale per i committenti senza una scelta esplicita e quando il
        prodotto non riesce a leggere la configurazione. Se il riferimento arriva lo stesso, EasyHelp lo scarta.
      </Alert>
      {committenti.length === 0 ? (
        <Typography color="text.secondary">
          Nessun committente: compaiono qui quelli dichiarati dai consumer del prodotto.
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small" aria-label="Regole per committente">
            <TableHead>
              <TableRow>
                <TableCell>Committente</TableCell>
                <TableCell>Riferimento al record consentito</TableCell>
                <TableCell>Ultima modifica</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {committenti.map((rule) => (
                <TableRow key={rule.committente}>
                  <TableCell>
                    <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                      <Typography component="span">{rule.committente}</Typography>
                      {rule.declaredBy !== 'consumer-attivo' && (
                        <Chip
                          size="small"
                          variant="outlined"
                          color={rule.declaredBy === 'nessuno' ? 'warning' : 'default'}
                          label={COMMITTENTE_DECLARATION_LABELS[rule.declaredBy]}
                        />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={rule.allowRecordReference}
                      disabled={busy}
                      onChange={(event) => onToggle(rule, event.target.checked)}
                      slotProps={{
                        input: { 'aria-label': `Riferimento al record consentito per ${rule.committente}` },
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {rule.updatedAt ? `${formatDateTime(rule.updatedAt)} · ${rule.updatedBy ?? ''}` : 'Default'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {committenti.some((rule) => rule.declaredBy === 'nessuno') && (
        <Typography variant="body2" color="text.secondary">
          «Non più dichiarato»: nessun consumer del prodotto indica più questo committente. La sua regola resta salvata
          e modificabile; se il committente torna in un consumer, eredita questa regola.
        </Typography>
      )}
      {error && <Alert severity="error">{error}</Alert>}
    </SettingsSection>
  );
}
